/**
 * ContractPage.tsx
 * Página pública para visualização e download do contrato de prestação de serviços.
 * Acessível em /contrato?salonId=:id ou /contrato (usa salão padrão)
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, FileText, Shield, CheckCircle2, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

interface ContractPreview {
  salonName: string;
  salonCnpj: string;
  text: string;
  generatedAt: string;
}

const CLAUSES = [
  { id: 1, title: "Objeto do Contrato", summary: "Descrição dos serviços de estética e beleza prestados" },
  { id: 2, title: "Agendamento e Confirmação", summary: "Como funciona o processo de reserva e confirmação" },
  { id: 3, title: "Cancelamento e Reagendamento", summary: "Regras para cancelar ou reagendar com antecedência mínima de 24h" },
  { id: 4, title: "Não Comparecimento (No-Show)", summary: "Consequências da ausência sem comunicação prévia" },
  { id: 5, title: "Preços e Condições de Pagamento", summary: "Formas de pagamento aceitas e política de preços" },
  { id: 6, title: "Responsabilidades da Contratada", summary: "Compromissos e limitações de responsabilidade do salão" },
  { id: 7, title: "Responsabilidades do Contratante", summary: "Obrigações do cliente, incluindo informações de saúde" },
  { id: 8, title: "Direito de Reclamação e Garantia", summary: "Prazo e condições para reclamações e correções" },
  { id: 9, title: "Uso de Imagem", summary: "Autorização para uso de fotos do resultado dos serviços" },
  { id: 10, title: "Proteção de Dados (LGPD)", summary: "Como seus dados são coletados, usados e protegidos" },
  { id: 11, title: "Conduta no Estabelecimento", summary: "Normas de comportamento e casos de recusa de atendimento" },
  { id: 12, title: "Vigência", summary: "Duração do contrato e processo de atualização dos termos" },
  { id: 13, title: "Resolução de Conflitos", summary: "Como resolver divergências de forma amigável ou judicial" },
  { id: 14, title: "Disposições Gerais", summary: "Cláusulas finais e disposições complementares" },
];

export default function ContractPage() {
  const [location] = useLocation();
  const [, navigate] = useLocation();
  const [preview, setPreview] = useState<ContractPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showFullText, setShowFullText] = useState(false);

  // Extrair salonId da query string
  const salonId = new URLSearchParams(window.location.search).get("salonId");

  useEffect(() => {
    const url = salonId
      ? `/api/contract/preview?salonId=${salonId}`
      : "/api/contract/preview";

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        setPreview(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Erro ao carregar o contrato. Tente novamente.");
      });
  }, [salonId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = salonId
        ? `/api/contract/download?salonId=${salonId}`
        : "/api/contract/download";

      const response = await fetch(url);
      if (!response.ok) throw new Error("Erro ao gerar PDF");

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `contrato-${preview?.salonName?.toLowerCase().replace(/\s+/g, "-") ?? "servicos"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      toast.success("Contrato baixado com sucesso!");
    } catch {
      toast.error("Erro ao baixar o contrato. Tente novamente.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Carregando contrato...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              className="text-muted-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Voltar
            </Button>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Contrato de Prestação de Serviços</span>
            </div>
          </div>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            size="sm"
            className="bg-primary text-primary-foreground"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Baixar PDF
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Hero */}
        <div className="text-center space-y-3 py-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-primary" />
            <Badge variant="outline" className="border-primary/30 text-primary">
              Documento Oficial
            </Badge>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Contrato de Prestação de Serviços
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm">
            Este contrato rege a relação entre{" "}
            <strong className="text-foreground">{preview?.salonName ?? "o salão"}</strong> e seus
            clientes, em conformidade com o Código de Defesa do Consumidor e a LGPD.
          </p>
          {preview?.salonCnpj && (
            <p className="text-xs text-muted-foreground">
              CNPJ: <span className="font-mono">{preview.salonCnpj}</span>
            </p>
          )}
        </div>

        {/* Destaques de proteção */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { icon: "🛡️", title: "Proteção Legal", desc: "Baseado no CDC e legislação brasileira vigente" },
            { icon: "🔒", title: "LGPD Compliant", desc: "Seus dados são tratados com segurança e transparência" },
            { icon: "✅", title: "Garantia de Qualidade", desc: "7 dias de garantia em serviços químicos e coloração" },
          ].map((item) => (
            <Card key={item.title} className="border-border/50 bg-card/50">
              <CardContent className="pt-4 pb-4">
                <div className="text-2xl mb-2">{item.icon}</div>
                <p className="text-sm font-semibold text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Índice de cláusulas */}
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Índice do Contrato
            </CardTitle>
            <CardDescription>
              Clique em cada cláusula para ver o resumo. Baixe o PDF para o texto completo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {CLAUSES.map((clause) => (
              <div
                key={clause.id}
                className="border border-border/50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpanded(expanded === clause.id ? null : clause.id)}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-secondary/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                      {clause.id}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      Cláusula {clause.id} — {clause.title}
                    </span>
                  </div>
                  {expanded === clause.id ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                </button>
                {expanded === clause.id && (
                  <div className="px-4 pb-3 pt-1 bg-secondary/10 border-t border-border/30">
                    <p className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                      {clause.summary}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Texto completo (colapsável) */}
        {preview?.text && (
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Texto Completo do Contrato</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullText(!showFullText)}
                  className="text-muted-foreground text-xs"
                >
                  {showFullText ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5 mr-1" />
                      Recolher
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5 mr-1" />
                      Expandir
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            {showFullText && (
              <CardContent>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed bg-secondary/10 p-4 rounded-lg overflow-auto max-h-[600px]">
                  {preview.text}
                </pre>
              </CardContent>
            )}
          </Card>
        )}

        {/* CTA de download */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
              <div className="flex-1">
                <p className="font-semibold text-foreground">Guarde uma cópia do seu contrato</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Baixe o PDF completo para ter sempre à mão. O documento inclui todas as cláusulas
                  e informações do salão.
                </p>
              </div>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-primary text-primary-foreground shrink-0"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Baixar Contrato em PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Rodapé */}
        <p className="text-center text-xs text-muted-foreground pb-4">
          Ao realizar qualquer agendamento junto a{" "}
          <strong>{preview?.salonName ?? "este salão"}</strong>, você declara ter lido e concordado
          com todos os termos deste contrato.
          {preview?.generatedAt && (
            <> Versão gerada em {new Date(preview.generatedAt).toLocaleDateString("pt-BR")}.</>
          )}
        </p>
      </div>
    </div>
  );
}
